import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import {
  fetchRegions, fetchPlacements, fetchPages, fetchTimeline, fetchPageTypeConfig,
  createPlacement, updatePlacement, deletePlacement, createPage, deletePage,
  reorderPlacements, fetchPageDraft, fetchPageDrafts, publishPage, revertPage, fetchDraftChanges,
  mintPreviewToken,
  ConflictError, Region, PlacementSummary, PageRecord, DraftChange
} from '../../services/api'
import { PAGE_TYPES, getPageTypeLabel, getPreviewPathForPageType } from '../../constants/pageTypes'
import { useWidgetSite } from '../../contexts/WidgetSiteContext'
import { useToast } from '../../contexts/ToastContext'
import { Badge, Button, Banner, ToggleGroup, useSpeedbump } from '../../components'
import RegionPanel from '../../components/merchandising/RegionPanel'
import PageSettingsModal from '../../components/merchandising/PageSettingsModal'
import TimelineScrubber, { AFTER_PUBLISH } from '../../components/merchandising/TimelineScrubber'
import PlacementDetail, { PlacementDetailInitialContext } from './PlacementDetail'
import utilStyles from '../../styles/utilities.module.scss'
import formStyles from '../../styles/forms.module.scss'
import pageStyles from '../../styles/page.module.scss'
import styles from './PageLayout.module.scss'
import rowStyles from './PlacementRows.module.scss'
import pvStyles from './PagePreview.module.scss'



const DEVICE_OPTIONS: { value: string; label: string; width: string }[] = [
  { value: 'desktop', label: 'Desktop', width: '100%' },
  { value: 'tablet', label: 'Tablet', width: '768px' },
  { value: 'mobile', label: 'Mobile', width: '375px' },
  { value: 'tv', label: 'TV', width: '1920px' },
]

// Base URL of the public Next.js site that the preview iframe loads,
// keyed by site code. Override per environment via VITE_TENNIS_COM_BASE_URL /
// VITE_TENNIS_CHANNEL_BASE_URL; local fallbacks match the tc-dev port map
// (scripts/tc-dev/src/apps.ts).
const PREVIEW_BASE_URLS: Record<string, string> = {
  tennis_com: import.meta.env.VITE_TENNIS_COM_BASE_URL || 'http://localhost:4000',
  tennis_channel: import.meta.env.VITE_TENNIS_CHANNEL_BASE_URL || 'http://localhost:4040',
}

// Re-mint preview tokens before the 15-min HMAC expiry kicks in.
const PREVIEW_TOKEN_REFRESH_MS = 12 * 60 * 1000

const VIEW_MODE_OPTIONS = [
  { value: 'placements' as const, label: 'Placements' },
  { value: 'preview' as const, label: 'Preview' },
]

type PageSelection =
  | { kind: 'all' }
  | { kind: 'page_type'; pageType: string }
  | { kind: 'custom_page'; page: PageRecord }

type ViewMode = 'placements' | 'preview'

const PageLayout: React.FC = () => {
  const { siteId, siteName, siteCode, setSite } = useWidgetSite()
  const { toast } = useToast()
  const confirm = useSpeedbump()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { pageType: routePageType, pageId: routePageId } = useParams<{ pageType?: string; pageId?: string }>()
  const isPreviewRoute = location.pathname.endsWith('/preview')

  const selection: PageSelection = useMemo(() => {
    if (routePageId) return { kind: 'custom_page', page: { id: routePageId } as PageRecord }
    if (routePageType) return { kind: 'page_type', pageType: routePageType }
    return { kind: 'all' }
  }, [routePageType, routePageId])

  const viewMode: ViewMode = isPreviewRoute ? 'preview' : 'placements'

  const setSelection = (sel: PageSelection) => {
    const base = sel.kind === 'page_type' ? `/content/${sel.pageType}`
      : sel.kind === 'custom_page' ? `/content/page/${sel.page.id}`
        : '/content'
    navigate(base)
  }

  const setViewMode = (mode: ViewMode) => {
    const base = selection.kind === 'page_type' ? `/content/${selection.pageType}`
      : selection.kind === 'custom_page' ? `/content/page/${selection.page.id}`
        : '/content'
    navigate(mode === 'preview' ? `${base}/preview` : base)
  }

  const [actionError, setActionError] = useState<string | null>(null)
  const [placementsSaving, setPlacementsSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [reverting, setReverting] = useState(false)

  const [settingsPage, setSettingsPage] = useState<PageRecord | null>(null)

  const [showNewPageForm, setShowNewPageForm] = useState(false)
  const [newPageName, setNewPageName] = useState('')
  const [newPagePath, setNewPagePath] = useState('')
  const [creatingPage, setCreatingPage] = useState(false)

  const [previewTimestamp, setPreviewTimestamp] = useState<string | null>(null)
  const [selectedDevice, setSelectedDevice] = useState('desktop')
  const [includeExpired, setIncludeExpired] = useState(false)

  const deviceConfig = DEVICE_OPTIONS.find(d => d.value === selectedDevice)!

  const { data: pageTypeConfig } = useSWR(
    siteId ? ['merch-page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false }
  )

  const { data: regionsData, error: regionsError } = useSWR(
    siteId ? ['merch-regions', siteId] : null,
    () => fetchRegions(siteId),
    { revalidateOnFocus: false }
  )
  const regions: Region[] = useMemo(() => regionsData?.regions || [], [regionsData])

  const { data: pagesData, mutate: mutatePages, error: pagesError } = useSWR(
    siteId ? ['merch-pages', siteId, 'custom'] : null,
    () => fetchPages(siteId, 'custom'),
    { revalidateOnFocus: false }
  )
  const customPages: PageRecord[] = useMemo(() => pagesData?.pages || [], [pagesData])

  // Live-only view, used solely to drive the "Inactive" badge. The list above
  // shows the highest version (a draft when one exists), so its is_active can
  // reflect an unpublished toggle. The badge must instead match what tennis.com
  // serves — the published row — while a pending change is signalled separately
  // by the draft dot.
  const { data: livePagesData, mutate: mutateLivePages } = useSWR(
    siteId ? ['merch-pages', siteId, 'custom', 'live'] : null,
    () => fetchPages(siteId, 'custom', false),
    { revalidateOnFocus: false }
  )
  const inactiveLivePageIds = useMemo(
    () => new Set((livePagesData?.pages || []).filter(p => !p.is_active).map(p => p.id)),
    [livePagesData]
  )

  const resolvedSelection: PageSelection = useMemo(() => {
    if (selection.kind === 'custom_page' && customPages.length > 0) {
      const fullPage = customPages.find(p => p.id === selection.page.id)
      if (fullPage) return { kind: 'custom_page', page: fullPage }
    }
    return selection
  }, [selection, customPages])

  const selectedPageType = selection.kind === 'page_type' ? selection.pageType
    : selection.kind === 'custom_page' ? 'custom'
      : undefined
  const selectedPageId = selection.kind === 'custom_page' ? selection.page.id : undefined

  const [stableNow, setStableNow] = useState(() => new Date().toISOString())
  const isPreview = viewMode === 'preview'

  // Refresh "now" when switching pages or toggling into preview
  useEffect(() => {
    setStableNow(new Date().toISOString())
  }, [selectedPageType, selectedPageId, isPreview])

  // Derive preview parameters from the single previewTimestamp value:
  // null = "Now" (live, published only)
  // AFTER_PUBLISH = "After publish" (now + drafts)
  // timestamp string = future time (always includes drafts — you're previewing what you're building)
  const previewIncludeDrafts = isPreview && previewTimestamp !== null
  const effectiveAsOf = isPreview
    ? (previewTimestamp === null || previewTimestamp === AFTER_PUBLISH ? stableNow : previewTimestamp)
    : undefined

  // Preview iframe is gated by a short-lived HMAC token minted by the API.
  // Re-mint before expiry so long preview sessions don't break.
  const [previewToken, setPreviewToken] = useState<{ token: string; expiresAt: number } | null>(null)
  useEffect(() => {
    if (!isPreview || !siteId) return
    let cancelled = false
    const refresh = async () => {
      try {
        const result = await mintPreviewToken()
        if (cancelled) return
        setPreviewToken({
          token: result.token,
          expiresAt: new Date(result.expires_at).getTime(),
        })
      } catch (err) {
        console.error('Failed to mint preview token', err)
      }
    }
    refresh()
    const id = setInterval(refresh, PREVIEW_TOKEN_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [isPreview, siteId])

  // Placements data drives the editor table only. In preview mode the iframe
  // renders the page directly via ACE, so we skip the fetch entirely —
  // schedule-aware preview rendering lives in ACE, not /placements.
  const { data: placementsData, mutate: mutatePlacements, error: placementsError, isValidating: placementsValidating } = useSWR(
    siteId && !isPreview ? ['merch-placements', siteId, selectedPageType, selectedPageId, includeExpired] : null,
    () => fetchPlacements({
      site_id: siteId,
      page_type: selectedPageType,
      page_id: selectedPageId,
      ...(includeExpired && { include_expired: true }),
    }),
    { revalidateOnFocus: false, dedupingInterval: 2000 }
  )
  const placements: PlacementSummary[] = useMemo(() => placementsData?.placements || [], [placementsData])

  // Timeline always includes drafts — the scrubber shows all future change points
  const { data: timelineData } = useSWR(
    siteId && isPreview ? ['merch-timeline', siteId, selectedPageType, selectedPageId] : null,
    () => fetchTimeline({
      site_id: siteId,
      page_type: selectedPageType,
      page_id: selectedPageId || undefined,
      include_drafts: true,
    }),
    { revalidateOnFocus: false }
  )

  // Draft status
  const { data: draftData, mutate: mutateDraft } = useSWR(
    siteId ? ['merch-page-draft', siteId, selectedPageType, selectedPageId] : null,
    () => fetchPageDraft({
      site_id: siteId,
      page_type: selectedPageType,
      page_id: selectedPageId,
    }),
    { revalidateOnFocus: false }
  )

  // All page drafts for the site — used to show draft indicators in the nav
  const { data: allDraftsData, mutate: mutateAllDrafts } = useSWR(
    siteId ? ['merch-page-drafts', siteId] : null,
    () => fetchPageDrafts(siteId),
    { revalidateOnFocus: false }
  )
  const draftScopes = useMemo(() => {
    const set = new Set<string>()
    for (const d of allDraftsData?.drafts || []) {
      // Key format: "page_type|page_id" — matches how we identify scopes
      set.add(`${d.page_type || ''}|${d.page_id || ''}`)
    }
    return set
  }, [allDraftsData])
  const hasDraftForScope = (pageType?: string, pageId?: string) =>
    draftScopes.has(`${pageType || ''}|${pageId || ''}`)

  // Track the draft concurrency token — updated from draftData and mutation responses
  const draftUpdatedAtRef = useRef<string | null>(null)
  useEffect(() => {
    draftUpdatedAtRef.current = draftData?.has_draft ? (draftData.updated_at ?? null) : ''
  }, [draftData])
  const getDraftToken = () => draftUpdatedAtRef.current ?? ''
  const updateDraftToken = (response: { draft_updated_at?: string | null }) => {
    if (response?.draft_updated_at) {
      draftUpdatedAtRef.current = response.draft_updated_at
    }
  }

  // Default preview to "After publish" when entering preview mode with drafts,
  // or reset when drafts are cleared (publish/revert). Don't override manual selection.
  const prevIsPreview = useRef(isPreview)
  const prevHasDraft = useRef(draftData?.has_draft)
  useEffect(() => {
    const justEnteredPreview = isPreview && !prevIsPreview.current
    const draftsJustAppeared = draftData?.has_draft && !prevHasDraft.current
    if ((justEnteredPreview || draftsJustAppeared) && draftData?.has_draft && previewTimestamp === null) {
      setPreviewTimestamp(AFTER_PUBLISH)
    } else if (draftData && !draftData.has_draft && previewTimestamp === AFTER_PUBLISH) {
      setPreviewTimestamp(null)
    }
    prevIsPreview.current = isPreview
    prevHasDraft.current = draftData?.has_draft
  }, [draftData, isPreview, previewTimestamp])

  const handleConflict = async () => {
    await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
    toast('This page was modified by another user. Your view has been refreshed — please try again.', 'error')
  }

  const handlePublishClick = async () => {
    let changes: DraftChange[] = []
    try {
      const data = await fetchDraftChanges({
        site_id: siteId,
        page_type: selectedPageType,
        page_id: selectedPageId,
      })
      changes = data.changes
    } catch {
      // If we can't fetch changes, proceed to publish anyway
    }

    const ok = await confirm({
      title: 'Publish changes',
      confirmLabel: 'Publish',
      ...(changes.length === 0
        ? { message: 'No pending changes found.' }
        : {
          children: (
            <>
              <p>The following changes will go live:</p>
              <ul>
                {changes.map((c, i) => (
                  <li key={i}>
                    {c.type === 'added' && <><strong>Add</strong> {c.name} to {c.region} at position {c.position}</>}
                    {c.type === 'modified' && c.details !== 'modified' && <><strong>Move</strong> {c.name} in {c.region} — {c.details}</>}
                    {c.type === 'modified' && c.details === 'modified' && <><strong>Update</strong> {c.name} in {c.region}</>}
                    {c.type === 'removed' && <><strong>Remove</strong> {c.name} from {c.region}</>}
                  </li>
                ))}
              </ul>
            </>
          ),
        }),
    })
    if (!ok) return

    setPublishing(true)
    try {
      await publishPage({
        site_id: siteId,
        page_type: selectedPageType || null,
        page_id: selectedPageId || null,
        draft_updated_at: getDraftToken(),
      })
      draftUpdatedAtRef.current = ''
      await Promise.all([mutatePages(), mutateLivePages(), mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      toast('Changes published')
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to publish', 'error')
    } finally {
      setPublishing(false)
    }
  }

  const handleRevert = async () => {
    const ok = await confirm({
      title: 'Revert changes',
      confirmLabel: 'Revert',
      confirmVariant: 'danger',
      message: 'This will discard all unpublished changes and restore the last published state. This cannot be undone.',
    })
    if (!ok) return

    setReverting(true)
    try {
      await revertPage({
        site_id: siteId,
        page_type: selectedPageType || null,
        page_id: selectedPageId || null,
        draft_updated_at: getDraftToken(),
      })
      draftUpdatedAtRef.current = ''
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      toast('Changes reverted')
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to revert', 'error')
    } finally {
      setReverting(false)
    }
  }

  // Group placements by (region_id, sort_order) and sort by starts_at within
  // each slot. Only consumed by the placements editor view; preview mode
  // doesn't fetch placements.
  const placementsByRegion = useMemo(() => {
    const map: Record<string, Record<number, PlacementSummary[]>> = {}
    for (const p of placements) {
      if (!map[p.region.id]) map[p.region.id] = {}
      if (!map[p.region.id][p.sort_order]) map[p.region.id][p.sort_order] = []
      map[p.region.id][p.sort_order].push(p)
    }
    for (const regionId of Object.keys(map)) {
      for (const pos of Object.keys(map[regionId])) {
        map[regionId][Number(pos)].sort((a, b) => {
          const aMs = a.starts_at ? a.starts_at.getTime() : 0
          const bMs = b.starts_at ? b.starts_at.getTime() : 0
          return aMs - bMs
        })
      }
    }
    return map
  }, [placements])

  const sortedRegions = useMemo(() => {
    const sorted = [...regions].sort((a, b) => a.sort_order - b.sort_order)
    if (!pageTypeConfig) return sorted
    const configKey = selectedPageType || '__all__'
    const allowedRegions = pageTypeConfig.page_types[configKey]
    if (!allowedRegions) return sorted
    return sorted.filter(r => allowedRegions.includes(r.code))
  }, [regions, pageTypeConfig, selectedPageType])

  const handleDuplicate = async (p: PlacementSummary) => {
    try {
      const result = await createPlacement({
        region_id: p.region.id,
        widget_id: p.widget.id,
        sort_order: p.sort_order,
        page_id: p.page?.id || undefined,
        page_type: p.page_type,
        slug: p.slug || undefined,
        device_targets: p.device_targets,
        starts_at: p.starts_at,
        ends_at: p.ends_at,
        is_active: false,
        draft_updated_at: getDraftToken(),
      })
      updateDraftToken(result)
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      toast('Placement duplicated')
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to duplicate', 'error')
    }
  }

  const handleDeletePlacement = async (p: PlacementSummary) => {
    const ok = await confirm({
      title: 'Delete placement',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      message: `Delete \u201c${p.name}\u201d? This will be staged as a draft change and take effect when you publish.`,
    })
    if (!ok) return
    try {
      const result = await deletePlacement(p.id, getDraftToken())
      updateDraftToken(result)
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      toast('Placement deleted')
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to delete', 'error')
    }
  }

  const handleToggleActive = async (p: PlacementSummary) => {
    try {
      const result = await updatePlacement(p.id, { is_active: !p.is_active, draft_updated_at: getDraftToken() })
      updateDraftToken(result)
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      toast(p.is_active ? 'Placement deactivated' : 'Placement activated')
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to update', 'error')
    }
  }

  const handlePin = async (placements: PlacementSummary[]) => {
    const isPinned = placements.some(p => p.pinned_position != null)
    setPlacementsSaving(true)
    let failed = 0
    let hitConflict = false
    try {
      for (const p of placements) {
        try {
          const result = await updatePlacement(p.id, {
            pinned_position: isPinned ? null : p.sort_order,
            draft_updated_at: getDraftToken(),
          })
          updateDraftToken(result)
        } catch (err) {
          if (err instanceof ConflictError) { hitConflict = true; break }
          failed++
        }
      }
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      if (hitConflict) return handleConflict()
      if (failed > 0) {
        toast(`${failed} of ${placements.length} placements failed to update`, 'error')
      } else {
        toast(isPinned ? 'Position unpinned' : `Position ${placements[0].sort_order + 1} pinned`)
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update', 'error')
    } finally {
      setPlacementsSaving(false)
    }
  }

  const handleReorder = async (regionId: string, items: Array<{ placement_id: string; sort_order: number }>) => {
    setPlacementsSaving(true)
    try {
      const result = await reorderPlacements(regionId, items, getDraftToken())
      updateDraftToken(result)
      await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
    } catch (err) {
      if (err instanceof ConflictError) return handleConflict()
      toast(err instanceof Error ? err.message : 'Failed to reorder', 'error')
    } finally {
      setPlacementsSaving(false)
    }
  }

  const handleCreatePage = async () => {
    if (!newPageName.trim() || !newPagePath.trim()) {
      setActionError('Page name and path are required')
      return
    }
    setCreatingPage(true)
    try {
      const created = await createPage({
        site_id: siteId,
        page_type: 'custom',
        path: newPagePath.trim().startsWith('/') ? newPagePath.trim() : `/${newPagePath.trim()}`,
        name: newPageName.trim(),
        is_custom: true,
      })
      mutatePages()
      mutateLivePages()
      setShowNewPageForm(false)
      setNewPageName('')
      setNewPagePath('')
      setSelection({ kind: 'custom_page', page: created })
      toast('Custom page created')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create page')
    } finally {
      setCreatingPage(false)
    }
  }

  const handleDeletePage = async (page: PageRecord) => {
    const ok = await confirm({
      title: 'Delete custom page',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
      message: `Delete custom page \u201c${page.name}\u201d? This is blocked if any placements still target the page.`,
    })
    if (!ok) return
    try {
      await deletePage(page.id)
      await Promise.all([mutatePages(), mutateLivePages(), mutatePlacements(), mutateDraft(), mutateAllDrafts()])
      if (selection.kind === 'custom_page' && selection.page.id === page.id) {
        setSelection({ kind: 'all' })
      }
      toast('Custom page deleted')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete page')
    }
  }

  const buildAddPlacementLink = (regionId: string, sortOrder: number): string => {
    const params = new URLSearchParams({ placement: 'new', region_id: regionId, sort_order: String(sortOrder) })
    if (selection.kind === 'page_type') params.set('page_type', selection.pageType)
    if (selection.kind === 'custom_page') {
      params.set('page_type', 'custom')
      params.set('page_id', selection.page.id)
    }
    return `?${params.toString()}`
  }

  // Placement drawer state, driven by the ?placement= query param
  const placementParam = searchParams.get('placement')
  const drawerOpen = placementParam !== null
  const drawerPlacementId = placementParam === 'new' ? null : placementParam
  const drawerContext: PlacementDetailInitialContext | undefined = drawerOpen ? {
    pageType: searchParams.get('page_type') || undefined,
    regionId: searchParams.get('region_id') || undefined,
    sortOrder: searchParams.get('sort_order') ? parseInt(searchParams.get('sort_order')!, 10) : undefined,
    pageId: searchParams.get('page_id') || undefined,
    slug: searchParams.get('slug') || undefined,
    startsAt: searchParams.get('starts_at') || undefined,
    pinnedPosition: searchParams.has('pinned_position') ? parseInt(searchParams.get('pinned_position')!, 10) : null,
  } : undefined

  const closeDrawer = () => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }

  const handlePlacementSaved = async () => {
    await Promise.all([mutatePlacements(), mutateDraft(), mutateAllDrafts()])
  }

  const isSelected = (sel: PageSelection) => {
    if (sel.kind !== selection.kind) return false
    if (sel.kind === 'page_type' && selection.kind === 'page_type') return sel.pageType === selection.pageType
    if (sel.kind === 'custom_page' && selection.kind === 'custom_page') return sel.page.id === selection.page.id
    return true
  }

  // Build the URL the preview iframe loads. Returns null when we can't
  // form one yet (no token, no selection, custom page not loaded). The
  // URL carries every preview knob — token, scrubber state, device,
  // pageType/pageId override — so a re-render fully refreshes the frame.
  const iframeUrl = useMemo(() => {
    if (!isPreview || !previewToken) return null
    if (resolvedSelection.kind === 'all') return null

    const baseUrl = PREVIEW_BASE_URLS[siteCode]
    if (!baseUrl) return null

    let path: string
    if (resolvedSelection.kind === 'custom_page') {
      if (!resolvedSelection.page.path) return null
      path = resolvedSelection.page.path
    } else {
      path = getPreviewPathForPageType(resolvedSelection.pageType)
    }

    const params = new URLSearchParams({
      preview: previewToken.token,
      device: selectedDevice,
    })
    if (effectiveAsOf) params.set('asOf', effectiveAsOf)
    if (previewIncludeDrafts) params.set('includeDrafts', 'true')
    // Page-type previews navigate to a real URL of that type (above), so their
    // merch context is derived from the path — no override needed. A custom page
    // may be renamed or unpublished, so the (site, path) lookup can miss; hand
    // ACE the page id directly.
    if (resolvedSelection.kind === 'custom_page') {
      params.set('previewPageType', 'custom')
      params.set('previewPageId', resolvedSelection.page.id)
    }

    return `${baseUrl}${path}?${params.toString()}`
  }, [isPreview, previewToken, resolvedSelection, selectedDevice, effectiveAsOf, previewIncludeDrafts, siteCode])

  const selectionLabel = (() => {
    if (resolvedSelection.kind === 'all') return 'All Pages'
    if (resolvedSelection.kind === 'page_type') return getPageTypeLabel(resolvedSelection.pageType)
    return resolvedSelection.page.name || 'Loading...'
  })()

  const fetchError = regionsError || pagesError || placementsError
  const isInitialLoading = !regionsData && !regionsError

  if (isInitialLoading) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.pageHeader}>
          <h1>Placements — {siteName}</h1>
        </div>
        <div className={pageStyles.loading}>
          <h3>Loading...</h3>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.headerButtons}>
        <Button variant="secondary" size="small" onClick={() => setSite('')}>
          Switch Site
        </Button>
      </div>
      <div className={pageStyles.pageHeader}>
        <h1>Placements — {siteName}</h1>
      </div>

      {fetchError && (
        <Banner variant="error" message={`Unable to load data: ${fetchError.message || 'Connection failed'}`} />
      )}

      {actionError && (
        <Banner variant="error" message={actionError} onDismiss={() => setActionError(null)} />
      )}

      <div className={styles.twoColumnLayout}>
        {/* Left column: page navigator */}
        <div className={styles.pageNav}>
          <div className={styles.pageNavSection}>
            <div className={styles.pageNavSectionLabel}>Pages</div>
            <button
              type="button"
              className={`${styles.pageNavItem} ${isSelected({ kind: 'all' }) ? styles.pageNavItemActive : ''}`}
              onClick={() => navigate('/content')}
            >
              All Pages
              {hasDraftForScope() && <span className={styles.draftDot} title="Unpublished changes" />}
            </button>
            {PAGE_TYPES.filter(pt => {
              // Config is per-site now — showing the full catalog while it
              // loads would flash page types the site doesn't have.
              if (!pageTypeConfig) return false
              const regions = pageTypeConfig.page_types[pt.value]
              return regions && regions.length > 0
            }).map(pt => (
              <button
                key={pt.value}
                type="button"
                className={`${styles.pageNavItem} ${isSelected({ kind: 'page_type', pageType: pt.value }) ? styles.pageNavItemActive : ''}`}
                onClick={() => setSelection({ kind: 'page_type', pageType: pt.value })}
              >
                {pt.label}
                {hasDraftForScope(pt.value) && <span className={styles.draftDot} title="Unpublished changes" />}
              </button>
            ))}
          </div>

          <div className={styles.pageNavSection}>
            <div className={styles.pageNavSectionLabel}>Custom Pages</div>
            {customPages.length === 0 && !showNewPageForm && (
              <div className={styles.pageNavEmpty}>No custom pages yet</div>
            )}
            {customPages.map(p => (
              <div
                key={p.id}
                className={`${styles.pageNavItem} ${styles.pageNavItemWithAction} ${isSelected({ kind: 'custom_page', page: p }) ? styles.pageNavItemActive : ''}`}
              >
                <button
                  type="button"
                  className={styles.pageNavItemButton}
                  onClick={() => setSelection({ kind: 'custom_page', page: p })}
                >
                  <span className={styles.pageNavItemNameRow}>
                    <span className={`${styles.pageNavItemName} ${utilStyles.textTruncate}`}>{p.name}</span>
                    {inactiveLivePageIds.has(p.id) && (
                      <span className={styles.inactiveBadge} title="Not active — this page returns a 404 on tennis.com">
                        <Badge variant="neutral">Inactive</Badge>
                      </span>
                    )}
                    {hasDraftForScope('custom', p.id) && <span className={styles.draftDot} title="Unpublished changes" />}
                  </span>
                  <span className={`${styles.pageNavItemPath} ${utilStyles.textTruncate}`}>{p.path}</span>
                </button>
                <button
                  type="button"
                  className={`${utilStyles.iconBtn} ${styles.pageNavSettingsBtn}`}
                  onClick={() => setSettingsPage(p)}
                  title="Page settings"
                >
                  &#9881;
                </button>
                <button
                  type="button"
                  className={`${utilStyles.iconBtn} ${utilStyles.iconBtnDanger} ${styles.pageNavDeleteBtn}`}
                  onClick={() => handleDeletePage(p)}
                  title="Delete custom page"
                >
                  &times;
                </button>
              </div>
            ))}
            {showNewPageForm ? (
              <div className={styles.newPageForm}>
                <input
                  type="text"
                  placeholder="Page name"
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="/path"
                  value={newPagePath}
                  onChange={(e) => setNewPagePath(e.target.value)}
                />
                <div className={styles.newPageFormActions}>
                  <Button size="small" onClick={handleCreatePage} disabled={creatingPage}>
                    {creatingPage ? 'Creating...' : 'Create'}
                  </Button>
                  <Button variant="secondary" size="small" onClick={() => { setShowNewPageForm(false); setNewPageName(''); setNewPagePath('') }}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={styles.pageNavAddBtn}
                onClick={() => setShowNewPageForm(true)}
              >
                + New Page
              </button>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className={styles.placementsPanel}>
          <div className={styles.placementsPanelHeader}>
            <div className={styles.headerTitle}>
              <h2>{selectionLabel}</h2>
              {resolvedSelection.kind === 'custom_page' && resolvedSelection.page.path && (
                <span className={styles.headerPath}>{resolvedSelection.page.path}</span>
              )}
            </div>
            <div className={styles.headerControls}>
              {viewMode === 'placements' && (
                <label className={formStyles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={includeExpired}
                    onChange={(e) => setIncludeExpired(e.target.checked)}
                  />
                  Show expired
                </label>
              )}
              {selection.kind !== 'all' && (
                <ToggleGroup
                  variant="toggle"
                  options={VIEW_MODE_OPTIONS}
                  value={viewMode}
                  onChange={(v) => {
                    setViewMode(v)
                    if (v === 'preview') setPreviewTimestamp(null)
                  }}
                />
              )}
            </div>
          </div>

          {/* ============ DRAFT BANNER ============ */}
          {draftData?.has_draft && (
            <Banner
              variant="warning"
              message={`Unpublished changes${draftData.updated_by ? ` by ${draftData.updated_by}` : ''}${draftData.updated_at ? ` — ${new Date(draftData.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date(draftData.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}`}
              action={
                <div className={formStyles.formActions}>
                  <Button size="small" variant="secondary" onClick={handleRevert} disabled={reverting || publishing}>
                    {reverting ? 'Reverting...' : 'Revert'}
                  </Button>
                  <Button size="small" onClick={handlePublishClick} disabled={publishing || reverting}>
                    {publishing ? 'Publishing...' : 'Publish'}
                  </Button>
                </div>
              }
            />
          )}

          {/* ============ PLACEMENTS VIEW ============ */}
          {viewMode === 'placements' && (
            <div className={rowStyles.layoutContainer} style={{ opacity: placementsValidating || placementsSaving ? 0.6 : 1, pointerEvents: placementsSaving ? 'none' : undefined, transition: 'opacity 0.15s' }}>
              {sortedRegions.length === 0 && (
                <div className={rowStyles.emptyRegion}>
                  <p>No regions found for this site.</p>
                </div>
              )}

              {sortedRegions.map(r => (
                <RegionPanel
                  key={r.id}
                  region={r}
                  placementsByPosition={placementsByRegion[r.id] || {}}
                  buildAddPlacementLink={buildAddPlacementLink}
                  onPin={handlePin}
                  onReorder={handleReorder}
                  onDuplicate={handleDuplicate}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDeletePlacement}
                />
              ))}
            </div>
          )}

          {/* ============ PREVIEW VIEW ============ */}
          {viewMode === 'preview' && (
            <div className={pvStyles.previewPanel}>
              <div className={pvStyles.previewControls}>
                <ToggleGroup
                  options={DEVICE_OPTIONS}
                  value={selectedDevice}
                  onChange={setSelectedDevice}
                />
              </div>

              <TimelineScrubber
                changePoints={timelineData?.change_points || []}
                now={timelineData?.now || stableNow}
                hasDraft={!!draftData?.has_draft}
                activeTimestamp={previewTimestamp}
                onTimestampChange={setPreviewTimestamp}
              />

              <div className={pvStyles.pvDeviceFrame} style={{ maxWidth: deviceConfig.width }}>
                {iframeUrl ? (
                  <iframe
                    src={iframeUrl}
                    title="Page preview"
                    className={pvStyles.pvIframePreview}
                    style={{ width: '100%', height: '900px', border: 'none', display: 'block', background: 'white' }}
                  />
                ) : (
                  <div className={pvStyles.pvIframePlaceholder} style={{ height: '900px' }}>
                    <span>{previewToken ? 'Select a page to preview' : 'Loading preview…'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PageSettingsModal
        page={settingsPage}
        draftUpdatedAt={getDraftToken()}
        onClose={() => setSettingsPage(null)}
        onSaved={async (updated) => {
          setSettingsPage(null)
          updateDraftToken(updated)
          await Promise.all([mutatePages(), mutateDraft(), mutateAllDrafts()])
          if (selection.kind === 'custom_page' && selection.page.id === updated.id) {
            setSelection({ kind: 'custom_page', page: updated })
          }
          toast('Page settings saved')
        }}
      />

      {drawerOpen && (
        <PlacementDetail
          key={placementParam}
          isOpen={drawerOpen}
          onClose={closeDrawer}
          onSaved={handlePlacementSaved}
          placementId={drawerPlacementId}
          initialContext={drawerContext}
        />
      )}
    </div>
  )
}

export default PageLayout
